import {
  setAllBrandsData,
  setBrandsMetadata,
  setBrandBackendRequirementsData,
  setBrandPropertiesData,
  setTestAgainPopupData,
  setTestAgainPopupMetadata,
} from "../../../features/brandsSlice";
import { setDisplayErrorSnackBar } from "../../../features/userSlice";
import { brandsService } from "../../../services/api/brands.endpoints";
import store from "../../../store";
// Note: these are still brandConfigurationReportSlice actions (Brands has no
// equivalent slice fields for overview/run-process data yet — forking that
// is a separate task from this endpoint/service-layer port). What changes
// here is only *which file makes the HTTP call* — BrandsEndPointLogic /
// brandsService instead of BrandConfigReportEndPointLogic /
// brandConfigReportService — not what state ends up populated.
import {
  setBrandAccountOverviewInfo,
  setBrandIndustryData,
  setBrandOverviewAdvisorSettingsData,
  setBrandOverviewFetcherSettingsData,
  setBrandOverviewLastRunData,
  setBrandOverviewUnifierSettingsData,
  setFetcherConfigData,
  setIsSuccess,
  setObjectSectionData,
  setObjectSectionMetaData,
  setOverviewMetaData,
  setOverviewPerformanceData,
  setProcessFailure,
  setRawTableData,
  setResWorkflowName,
} from "../../../features/brandConfigurationReportSlice";

async function getAllBrandsData(
  oauthTokenHeader: string,
  queryId: string | any,
  website_shared_uids: string[],
  type: string | any,
) {
  try {
    const response = await brandsService.getAllBrandsData(
      oauthTokenHeader,
      queryId,
      website_shared_uids,
      type
    );
    if (!response || !response.status) {
      throw new Error("Failed to access brands data.");
    } else if (response.status === 200) {
      const brandsData = response?.data?.data?.query_output ?? [];
      const brandsMetadata = response?.data?.data?.output_metadata ?? {};

      store.dispatch(setAllBrandsData(brandsData)); // Set the fetched data
      store.dispatch(setBrandsMetadata(brandsMetadata)); // set the metadata
    }
  } catch (e: any) {
    const status = e?.status || 500;
    store.dispatch(
      setDisplayErrorSnackBar({
        message: e?.parsedText || "Unknown error occurred",
        type: "error",
      }),
    );
    return status;
  }
}

// Single-brand "Try to Connect" (per-row quick action on Not Connected rows).
// The bulk multi-select "Try to Connect" button (Not Connected tab) reuses the
// same function with more than one id.
async function tryToConnect(oauthTokenHeader: string, brandIds: number[]) {
  try {
    const response = await brandsService.tryToConnect(oauthTokenHeader, {
      brands: brandIds,
    });
    if (!response || response.status !== 200) {
      throw new Error("Failed to connect brand.");
    }
    return response;
  } catch (e: any) {
    store.dispatch(
      setDisplayErrorSnackBar({
        message: e?.parsedText || "Failed to connect brand.",
        type: "error",
      }),
    );
    throw e;
  }
}

// --- Below: ported verbatim (same request structure / same dispatches) from
// BrandConfigReportEndPointLogic.ts, so files under the Brands module call
// through this file instead of the legacy one. BrandConfigReportEndPointLogic
// itself is untouched. ---

async function updateBrandProperties(
  oauthTokenHeader: string,
  data: any,
  brand_shared_uid: any,
) {
  try {
    const response = await brandsService.editBrandProperties(
      oauthTokenHeader,
      data,
      brand_shared_uid,
    );

    if (!response) {
      throw new Error("Failed to update brand properties.");
    }

    if (response.status === 200) {
      return response;
    }

    if (response.status === 207) {
      const errorMessage =
        "Failed to update brand properties. Please try again.";
      throw new Error(errorMessage);
    }

    if (response.status === 400) {
      throw new Error(
        response?.data?.reason ||
          "Something went wrong; please try again later...",
      );
    }

    if (response.status === 502) {
      throw new Error(
        "Server is temporarily unavailable. Please try again later.",
      );
    }

    throw new Error(
      response?.data?.message || "Failed to update brand properties.",
    );
  } catch (e: any) {
    throw e;
  }
}

async function postBrandConfigurationProcess(
  oauthTokenHeader: string,
  data: any,
) {
  try {
    const response = await brandsService.postBrandConfigurationReport(
      oauthTokenHeader,
      data,
    );
    if (!response || !response.status) {
      throw new Error("Failed to process brand integration.");
    } else if (response.status === 200) {
      return response;
    } else if (response.status === 400) {
      throw new Error("Bad request - invalid brand configuration data.");
    } else if (response.status === 502) {
      throw new Error("Bad gateway - service temporarily unavailable.");
    }
  } catch (e: any) {
    store.dispatch(
      setDisplayErrorSnackBar({
        message:
          e?.parsedText || e?.message || "Failed to process brand integration.",
        type: "error",
      }),
    );
  }
}

async function getBrandOverviewLastRuns(
  oauthTokenHeader: string | any,
  brand_id: string | any,
) {
  try {
    const response = await brandsService.getBrandOverviewLastRuns(
      oauthTokenHeader,
      brand_id,
    );
    if (!response || !response.status) {
      throw new Error("Failed to access brand last run information.");
    } else if (response.status === 200) {
      const brandOverviewLastRun = response?.data?.data ?? [];
      store.dispatch(setBrandOverviewLastRunData(brandOverviewLastRun));
    }
  } catch (e: any) {
    store.dispatch(
      setDisplayErrorSnackBar({
        message:
          e?.parsedText || "Failed to access brand last run information.",
        type: "error",
      }),
    );
  }
}

async function getBrandOverviewAdvisorSettings(
  oauthTokenHeader: string,
  brand_id: string | any,
  report: string | any,
) {
  try {
    const response = await brandsService.getBrandOverviewAdvisorSettings(
      oauthTokenHeader,
      brand_id,
      report,
    );
    if (!response || !response.status) {
      throw new Error("Failed to access brand advisor settings.");
    } else if (response.status === 200) {
      const brandOverviewAdvisorSettings = response?.data?.data ?? [];
      store.dispatch(
        setBrandOverviewAdvisorSettingsData({
          reportType: report,
          data: brandOverviewAdvisorSettings,
        }),
      );
    }
  } catch (e: any) {
    store.dispatch(
      setDisplayErrorSnackBar({
        message: e?.parsedText || "Failed to access brand advisor settings.",
        type: "error",
      }),
    );
  }
}

async function getBrandOverviewUnifierSettings(
  oauthTokenHeader: string,
  brand_id: string | any,
  report: string | any,
) {
  try {
    const response = await brandsService.getBrandOverviewUnifierSettings(
      oauthTokenHeader,
      brand_id,
      report,
    );
    if (!response || !response.status) {
      throw new Error("Failed to access brand unifier settings.");
    } else if (response.status === 200) {
      const brandOverviewUnifierSettings = response?.data?.data ?? [];
      store.dispatch(
        setBrandOverviewUnifierSettingsData({
          reportType: report,
          data: brandOverviewUnifierSettings,
        }),
      );
      return brandOverviewUnifierSettings;
    }
  } catch (e: any) {
    store.dispatch(
      setDisplayErrorSnackBar({
        message: e?.parsedText || "Failed to access brand unifier settings.",
        type: "error",
      }),
    );
  }
}

async function getBrandOverviewFetcherSettings(
  oauthTokenHeader: string,
  reqBody: any,
  report: string | any,
) {
  try {
    const response = await brandsService.getBrandOverviewFetcherSettings(
      oauthTokenHeader,
      reqBody,
    );
    if (!response || !response.status) {
      throw new Error("Failed to access brand fetcher settings.");
    } else if (response.status === 200) {
      const brandOverviewFetcherSettings = response?.data?.data ?? [];
      store.dispatch(
        setBrandOverviewFetcherSettingsData({
          reportType: report,
          data: brandOverviewFetcherSettings,
        }),
      );
    }
  } catch (e: any) {
    store.dispatch(
      setDisplayErrorSnackBar({
        message: e?.parsedText || "Failed to access brand fetcher settings.",
        type: "error",
      }),
    );
  }
}

async function brandsRunProcess(oauthTokenHeader: string, data: any) {
  try {
    const response = await brandsService.brandsRunProcess(
      oauthTokenHeader,
      data,
    );
    if (!response.status || response.status !== 200) {
      store.dispatch(setIsSuccess(false));
      store.dispatch(
        setProcessFailure({
          isFailed: true,
          message: "Something went wrong while running brand process",
        }),
      );
    } else {
      store.dispatch(setProcessFailure({ isFailed: false, message: "" }));
      store.dispatch(setIsSuccess(true));
      const workflowName = response?.data?.data?.workflow_name;
      if (workflowName) {
        store.dispatch(setResWorkflowName(workflowName));
      } else {
        store.dispatch(
          setDisplayErrorSnackBar({
            message: "Workflow name not found in the response",
            type: "error",
          }),
        );
      }
    }
  } catch (e: any) {
    store.dispatch(
      setProcessFailure({
        isFailed: true,
        message: e?.parsedText || "Unknown error occurred",
      }),
    );
  }
}

async function getOverviewPerformance(
  oauthTokenHeader: string | any,
  report: string | any,
  queryId: string | any,
  website_shared_uid: string | any,
  type: string | any,
  brand_id: string | any,
  start_date: string | any,
  end_date: string | any,
  limit: string | any,
) {
  try {
    const response = await brandsService.getOverviewPerformance(
      oauthTokenHeader,
      queryId,
      website_shared_uid,
      type,
      brand_id,
      start_date,
      end_date,
      limit,
    );
    if (!response || !response.status) {
      throw new Error("Failed to access overview performance.");
    } else if (response.status === 200) {
      const overViewData = response?.data?.data?.query_output ?? [];
      const overviewMetadata = response?.data?.data?.output_metadata ?? {};

      store.dispatch(
        setOverviewPerformanceData({
          reportType: report,
          data: overViewData,
        }),
      );

      store.dispatch(
        setOverviewMetaData({ reportType: report, data: overviewMetadata }),
      );

      return response.status;
    }
  } catch (e: any) {
    const status = e?.status || 500;
    store.dispatch(
      setDisplayErrorSnackBar({
        message: e?.parsedText || "Unknown error occurred",
        type: "error",
      }),
    );
    return status;
  }
}

async function getObjectsData(
  oauthTokenHeader: string,
  queryId: string | any,
  website_shared_uid: string | any,
  type: string | any,
  brand_id: string | any,
) {
  try {
    const response = await brandsService.getObjectsData(
      oauthTokenHeader,
      queryId,
      website_shared_uid,
      type,
      brand_id,
    );
    if (!response || !response.status) {
      throw new Error("Failed to access objects data.");
    } else if (response.status === 200) {
      const objectsData = response?.data?.data?.query_output ?? [];
      const objectsMetadata = response?.data?.data?.output_metadata ?? {};

      store.dispatch(setObjectSectionData(objectsData));
      store.dispatch(setObjectSectionMetaData(objectsMetadata));
    }
  } catch (e: any) {
    const status = e?.status || 500;
    store.dispatch(
      setDisplayErrorSnackBar({
        message: e?.parsedText || "Unknown error occurred",
        type: "error",
      }),
    );
    return status;
  }
}

async function getBrandAccountOverviewInfo(
  oauthTokenHeader: string,
  queryId: string | any,
  website_shared_uid: string | any,
  type: string | any,
  brand_id: string | any,
) {
  try {
    const response = await brandsService.getBrandAccountOverviewInfo(
      oauthTokenHeader,
      queryId,
      website_shared_uid,
      type,
      brand_id,
    );
    if (!response || !response.status) {
      throw new Error("Failed to access brand account overview data.");
    } else if (response.status === 200) {
      const brandAccountOverviewInfo = response?.data?.data?.query_output ?? [];
      store.dispatch(setBrandAccountOverviewInfo(brandAccountOverviewInfo));
    }
  } catch (e: any) {
    const status = e?.status || 500;
    store.dispatch(
      setDisplayErrorSnackBar({
        message:
          e?.parsedText || "Failed to access brand account overview data.",
        type: "error",
      }),
    );
    return status;
  }
}

async function getAllConfigForFetcher(
  oauthTokenHeader: string,
  biz_layer_query_id: any,
  website_shared_uid: any,
) {
  try {
    const response = await brandsService.getAllConfigForFetcher(
      oauthTokenHeader,
      biz_layer_query_id,
      website_shared_uid,
    );
    if (!response || !response.status) {
      throw new Error("Failed to access fetcher config.");
    } else if (response.status === 200) {
      const fetcherConfig = response?.data?.data ?? [];
      store.dispatch(setFetcherConfigData(fetcherConfig));
    }
  } catch (e: any) {
    store.dispatch(
      setDisplayErrorSnackBar({
        message: e?.parsedText || "Failed to access fetcher config.",
        type: "error",
      }),
    );
  }
}

async function getBrandsForIndustry(
  oauthTokenHeader: string,
  biz_layer_query_id: any,
  website_shared_uid: any,
) {
  try {
    // Matches BrandConfigReportEndPointLogic.getBrandsForIndustry exactly —
    // it also calls the "getAllConfigForFetcher" service function, not a
    // distinct one. Preserved as-is rather than "fixed", per instructions.
    const response = await brandsService.getAllConfigForFetcher(
      oauthTokenHeader,
      biz_layer_query_id,
      website_shared_uid,
    );
    if (!response || !response.status) {
      throw new Error("Failed to access Industry data.");
    } else if (response.status === 200) {
      const data = response?.data?.data ?? [];
      store.dispatch(setBrandIndustryData(data));
    }
  } catch (e: any) {
    store.dispatch(
      setDisplayErrorSnackBar({
        message: e?.parsedText || "Failed to access Industry data.",
        type: "error",
      }),
    );
  }
}

async function getRawTableData(
  oauthTokenHeader: string,
  table_id: string | any,
  start_date: string | any,
  end_date: string | any,
  type: string | any,
  fieldMappingId?: string | any,
) {
  try {
    const response = await brandsService.getRawTableData(
      oauthTokenHeader,
      table_id,
      start_date,
      end_date,
      type,
      fieldMappingId,
    );
    if (!response || !response.status) {
      throw new Error("Failed to access raw table data.");
    } else if (response.status === 200) {
      const rawTableData = response?.data?.data ?? [];
      store.dispatch(setRawTableData(rawTableData));
    }
  } catch (e: any) {
    const status = e?.status || 500;
    store.dispatch(
      setDisplayErrorSnackBar({
        message: e?.parsedText || "Unknown error occurred",
        type: "error",
      }),
    );
    return status;
  }
}

// --- Below: three more functions ported verbatim from
// BrandConfigReportEndPointLogic.ts (getBrandBackendRequirements,
// getBrandProperties, getTestAgainPopupData, manuallyApprove), dispatching
// into brandsSlice's own fields instead of brandConfigurationReportSlice's —
// these power the Brands-owned useBrandProperties.ts hook and
// ValidationStatusDialog.tsx (Edit Properties / Validation Station), which
// no longer read the legacy slice. ---

async function getBrandBackendRequirements(
  oauthTokenHeader: string,
  backend_shared_uid: string,
) {
  try {
    const response = await brandsService.getBrandBackendRequirements(
      oauthTokenHeader,
      backend_shared_uid,
    );
    if (!response || !response.status) {
      throw new Error(
        "Failed to access brand configuration backend requirements.",
      );
    } else if (response.status === 200) {
      const backendRequirements = response?.data?.data ?? [];
      store.dispatch(setBrandBackendRequirementsData(backendRequirements));
    }
  } catch (e: any) {
    store.dispatch(
      setDisplayErrorSnackBar({
        message:
          e?.parsedText ||
          "Failed to access brand configuration backend requirements.",
        type: "error",
      }),
    );
  }
}

async function getBrandProperties(
  oauthTokenHeader: string,
  brand_shared_uid: string,
) {
  try {
    const response = await brandsService.getBrandProperties(
      oauthTokenHeader,
      brand_shared_uid,
    );
    if (!response || !response.status) {
      throw new Error("Failed to access brand properties.");
    } else if (response.status === 200) {
      const brandProperties = response?.data?.data ?? [];
      store.dispatch(setBrandPropertiesData(brandProperties));
    }
  } catch (e: any) {
    store.dispatch(
      setDisplayErrorSnackBar({
        message: e?.parsedText || "Failed to access brand properties.",
        type: "error",
      }),
    );
  }
}

async function getTestAgainPopupData(
  oauthTokenHeader: string,
  queryId: string | any,
  website_shared_uid: string | any,
  type: string | any,
  brand_id: string | any,
) {
  try {
    const response = await brandsService.getTestAgainPopupData(
      oauthTokenHeader,
      queryId,
      website_shared_uid,
      type,
      brand_id,
    );
    if (!response || !response.status) {
      throw new Error("Failed to access data.");
    } else if (response.status === 200) {
      const popupData = response?.data?.data?.query_output ?? [];
      const popupMetadata = response?.data?.data?.output_metadata ?? {};
      store.dispatch(setTestAgainPopupData(popupData));
      store.dispatch(setTestAgainPopupMetadata(popupMetadata));
    }
  } catch (e: any) {
    const status = e?.status || 500;
    store.dispatch(
      setDisplayErrorSnackBar({
        message: e?.parsedText || "Failed to access data.",
        type: "error",
      }),
    );
    return status;
  }
}

async function manuallyApprove(
  oauthTokenHeader: string,
  data: any,
  brand_id: string | any,
) {
  try {
    const response = await brandsService.manuallyApprove(
      oauthTokenHeader,
      data,
      brand_id,
    );
    if (!response || !response.status) {
      throw new Error("Failed to approve the validation.");
    } else if (response.status === 200) {
      return response;
    }
  } catch (e: any) {
    const status = e?.status || 500;
    return status;
  }
}

export const BrandsEndPointLogic = {
  getAllBrandsData,
  tryToConnect,
  updateBrandProperties,
  postBrandConfigurationProcess,
  getBrandOverviewLastRuns,
  getBrandOverviewAdvisorSettings,
  getBrandOverviewUnifierSettings,
  getBrandOverviewFetcherSettings,
  brandsRunProcess,
  getOverviewPerformance,
  getObjectsData,
  getBrandAccountOverviewInfo,
  getAllConfigForFetcher,
  getBrandsForIndustry,
  getRawTableData,
  getBrandBackendRequirements,
  getBrandProperties,
  getTestAgainPopupData,
  manuallyApprove,
};
